import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ActivationTag } from '@/src/GalleryConfiguration/components/hooks/useSlideshows';
import { TextInput } from '../../../components/forms';

/* Small pieces shared by the gallery page sections. */

// Mirrors cloud/modules/mediaTags/service so chips match what the server stores.
export function normalizeTag(tag: string): string {
    return tag.trim().replace(/\s+/g, ' ').toLowerCase();
}

export const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const displayUrl = (u: string) => u.replace(/^https?:\/\//, '');

/** Copy-to-clipboard with a short "Copied" state per key. */
export function useCopy() {
    const [copied, setCopied] = useState<string | null>(null);
    const timer = useRef<number>();
    useEffect(() => () => window.clearTimeout(timer.current), []);
    const copy = (key: string, text: string) => {
        navigator.clipboard
            ?.writeText(text)
            .then(() => {
                setCopied(key);
                window.clearTimeout(timer.current);
                timer.current = window.setTimeout(() => setCopied(null), 1500);
            })
            .catch(() => {});
    };
    return { copied, copy };
}

export const IconCopy = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="7" y="7" width="9" height="9" rx="2" />
        <path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    </svg>
);

export const IconExternal = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3M12 4h4v4M16 4l-7 7" />
    </svg>
);

/** One public link: name, address, copy + open. */
export function LinkRow({
    title,
    href,
    note,
    copied,
    onCopy,
    extra,
}: {
    title: string;
    href: string;
    note?: string;
    copied: boolean;
    onCopy: () => void;
    extra?: React.ReactNode;
}) {
    return (
        <div className="trow2 gl-link">
            <span className="gl-link-t">
                <span className="nm2">{title}</span>
                <span className="sub2 ev-mono gl-url">{href ? displayUrl(href) : '—'}</span>
                {note && <span className="sub2">{note}</span>}
            </span>
            <span className="gl-link-a">
                {href && (
                    <>
                        <button
                            type="button"
                            className={copied ? 'gl-ic on' : 'gl-ic'}
                            aria-label={copied ? `${title} link copied` : `Copy ${title} link`}
                            title={copied ? 'Copied' : 'Copy'}
                            onClick={onCopy}
                        >
                            <IconCopy />
                        </button>
                        <a className="gl-ic" href={href} target="_blank" rel="noreferrer" aria-label={`Open ${title}`} title="Open">
                            <IconExternal />
                        </a>
                    </>
                )}
                {extra}
            </span>
        </div>
    );
}

/** Native colour swatch + hex text, side by side. Empty = unset. */
export function ColorField({
    label,
    value,
    disabled,
    error,
    onChange,
}: {
    label: string;
    value: string;
    disabled?: boolean;
    error?: string;
    onChange: (hex: string) => void;
}) {
    /* <input type=color> only takes #rrggbb */
    const full = HEX.test(value)
        ? value.length === 4
            ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
            : value
        : '#ffffff';
    return (
        <div className="field gl-color">
            <span className="field-l">{label}</span>
            <span className="gl-color-row">
                <input
                    type="color"
                    className="gl-swatch"
                    aria-label={`${label} colour picker`}
                    value={full.toLowerCase()}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                />
                <TextInput
                    className="input ev-mono"
                    aria-label={`${label} hex value`}
                    aria-invalid={error ? true : undefined}
                    placeholder="#rrggbb"
                    maxLength={7}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value.trim())}
                />
            </span>
            {error && <span className="field-e">{error}</span>}
        </div>
    );
}

/** Image field: preview + Choose (shared picker) + Remove. */
export function AssetField({
    label,
    value,
    disabled,
    onPick,
    onClear,
}: {
    label: string;
    value: string;
    disabled?: boolean;
    onPick: () => void;
    onClear: () => void;
}) {
    return (
        <div className="gl-asset">
            <span className="field-l">{label}</span>
            <span className="gl-asset-im">
                {value ? <img src={value} alt={`${label} preview`} /> : <span className="quiet-empty">None</span>}
            </span>
            <span className="gl-asset-a">
                <button type="button" className="tb-btn sm" disabled={disabled} onClick={onPick} aria-label={`Choose ${label}`}>
                    {value ? 'Replace' : 'Choose'}
                </button>
                {value && (
                    <button type="button" className="tb-btn ghost sm" disabled={disabled} onClick={onClear} aria-label={`Remove ${label}`}>
                        Remove
                    </button>
                )}
            </span>
        </div>
    );
}

/** Tag chips + free-form entry + suggestions from the activation's tags. */
export function TagEditor({
    label,
    tags,
    suggestions,
    disabled,
    onChange,
    draft,
    onDraft,
}: {
    label: string;
    tags: string[];
    suggestions: ActivationTag[];
    disabled?: boolean;
    onChange: (tags: string[]) => void;
    /* draft is lifted so a parent can fold an un-entered tag in on save */
    draft: string;
    onDraft: (v: string) => void;
}) {
    const push = () => {
        const t = normalizeTag(draft);
        onDraft('');
        if (!t || tags.includes(t)) return;
        onChange([...tags, t]);
    };
    const open = suggestions.filter((t) => !tags.includes(t.tag)).slice(0, 12);
    return (
        <div className="gl-tags">
            <div className="gl-chips">
                {tags.map((t) => (
                    <span className="pchip gl-tag" key={t}>
                        {t}
                        <button
                            type="button"
                            className="gl-x"
                            aria-label={`Remove tag ${t}`}
                            disabled={disabled}
                            onClick={() => onChange(tags.filter((x) => x !== t))}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <TextInput
                    className="input gl-tag-in"
                    aria-label={label}
                    placeholder="Type a tag, Enter"
                    value={draft}
                    disabled={disabled}
                    onChange={(e) => onDraft(e.target.value)}
                    onBlur={push}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            push();
                        }
                    }}
                />
            </div>
            {open.length > 0 && (
                <div className="gl-chips gl-sugg" aria-label="Tags used in this event">
                    {open.map((t) => (
                        <button
                            type="button"
                            key={t.tag}
                            className="gl-sugg-b"
                            disabled={disabled}
                            onClick={() => onChange([...tags, t.tag])}
                        >
                            + {t.tag} <span className="n">{t.count}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Section header status: saving / failed / saved. */
export function SectionStatus({ state }: { state: 'idle' | 'saving' | 'failed' | 'saved' }) {
    if (state === 'idle') return null;
    return (
        <span className={state === 'failed' ? 'gl-status bad' : 'gl-status'} role={state === 'failed' ? 'alert' : 'status'}>
            {state === 'saving' ? 'Saving…' : state === 'failed' ? 'Didn’t save — try again' : 'Saved'}
        </span>
    );
}

/** Batched on/off row with a sub-line (rides a save button, not immediate). */
export function CheckOpt({
    label,
    sub,
    checked,
    disabled,
    onChange,
}: {
    label: string;
    sub?: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (on: boolean) => void;
}) {
    return (
        <label className="mrow-t gl-opt">
            <span>
                <span className="lbl">{label}</span>
                {sub && (
                    <>
                        <br />
                        <span className="sub3">{sub}</span>
                    </>
                )}
            </span>
            <span className="tg">
                <span className="check">
                    <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
                    <span className="box" aria-hidden="true">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="m5 12 4 4L19 6" />
                        </svg>
                    </span>
                </span>
            </span>
        </label>
    );
}
